import { useCallback, useRef, useState } from "react";
import { useFetchMxfNestApi, useMxfNestApi } from "hooks/useMxfApiNest";
import { useClientsAsync } from "useCases/suporte/useControlPainel";
import { cleanObject } from "utils";

// POST /mxf/v1/consultar_produtos — resposta: { totalizador: [{ status_novo, count }] }
export const useConsultaProdutosTotais = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const api = useMxfNestApi();
  // A consulta pode levar minutos: duas buscas seguidas podem responder fora de
  // ordem, então só a última pedida escreve o resultado.
  const buscaAtual = useRef(0);

  const fetch = useCallback(
    async ({ idCliente, codigos }) => {
      const idBusca = ++buscaAtual.current;
      setIsLoading(true);
      setError(null);
      try {
        const response = await api.post(
          "/consultar_produtos",
          cleanObject({ id_cliente: idCliente, codigos }),
        );
        if (idBusca !== buscaAtual.current) return null;
        setData(response.data);
        return response.data;
      } catch (err) {
        if (idBusca === buscaAtual.current) setError(err);
        return null;
      } finally {
        if (idBusca === buscaAtual.current) setIsLoading(false);
      }
    },
    [api],
  );
  return { data, isLoading, error, fetch };
};

// POST /mxf/v1/consultar_produtos/listagem — exige códigos ou status
export const useConsultaProdutosLista = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const api = useMxfNestApi();
  const buscaAtual = useRef(0);

  const fetch = useCallback(
    async ({ idCliente, codigos, status, pagina = 1, limite = 10 }) => {
      const idBusca = ++buscaAtual.current;
      setIsLoading(true);
      setError(null);
      try {
        const response = await api.post(
          "/consultar_produtos/listagem",
          cleanObject({
            id_cliente: idCliente,
            codigos,
            status,
            pagina,
            limite,
          }),
        );
        if (idBusca !== buscaAtual.current) return null;
        setData(response.data);
        return response.data;
      } catch (err) {
        if (idBusca === buscaAtual.current) setError(err);
        return null;
      } finally {
        if (idBusca === buscaAtual.current) setIsLoading(false);
      }
    },
    [api],
  );
  return { data, isLoading, error, fetch };
};

// GET /mxf/v1/consultar_produtos/status — opções do combo Status
export const useConsultaProdutosStatus = () =>
  useFetchMxfNestApi("/consultar_produtos/status");

export const useClientesOption = () => {
  const { fetchClients } = useClientsAsync();

  // O combo do cockpit lista cli_nome_fantasia (consultar_produtos.php), mas quem
  // digita a razão social também precisa achar — a rota filtra um campo por vez,
  // então buscamos os dois e juntamos. O limit evita o padrão de 10 por página.
  const clienteOption = async value => {
    if (!value) return [];

    const [porFantasia, porRazao] = await Promise.all([
      fetchClients({ cli_nome_fantasia: value, limit: 50 }),
      fetchClients({ cli_nome: value, limit: 50 }),
    ]);

    const encontrados = new Map();
    [...(porFantasia?.data ?? []), ...(porRazao?.data ?? [])].forEach(item => {
      if (item?.cli_id && !encontrados.has(item.cli_id)) {
        encontrados.set(item.cli_id, item);
      }
    });

    return [...encontrados.values()].map(item => ({
      label: item.cli_nome_fantasia || item.cli_nome,
      value: item.cli_id,
    }));
  };
  return { clienteOption };
};
